import { HomeSolutions } from '../../productEntry/surface';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Bell, Bot, CheckCircle2, Clock, FileText, Plug, Search,
  Sparkles, Workflow,
} from 'lucide-react';
import { pipelinesApi, type PipelineInstance } from '../../api/pipelinesApi';
import { agentsApi } from '../../api/agentsApi';
import { tasksApi } from '../../api/tasksApi';
import { remindersApi } from '../../api/remindersApi';
import type { Agent } from '../../types/contracts';
import { haptic } from '../../telegram';

function webUser(): { name: string; photo?: string } {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const user = w.__OA_WEB_USER__ || w?.Telegram?.WebApp?.initDataUnsafe?.user;
    if (user) return { name: user.first_name || user.username || 'друг', photo: user.photo_url };
  } catch { /* ignore */ }
  return { name: 'друг' };
}

function relativeDue(due?: string | null): string {
  if (!due) return 'без срока';
  const time = new Date(due).getTime();
  const diffMinutes = Math.round((time - Date.now()) / 60_000);
  if (diffMinutes >= 0 && diffMinutes < 60) return `через ${diffMinutes || 1} мин`;
  if (diffMinutes >= 60 && diffMinutes < 1440) return `через ${Math.round(diffMinutes / 60)} ч`;
  return new Date(time).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

interface Upcoming {
  id: string;
  kind: 'task' | 'reminder';
  title: string;
  due?: string | null;
  to: string;
}

export function Home() {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<PipelineInstance[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [upcoming, setUpcoming] = useState<Upcoming[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const user = webUser();

  useEffect(() => {
    Promise.allSettled([pipelinesApi.list(), agentsApi.list(), tasksApi.list(), remindersApi.list()]).then(([p, a, tasks, reminders]) => {
      if (p.status === 'fulfilled') setPipelines(p.value || []);
      else setHasError(true);
      if (a.status === 'fulfilled') setAgents(a.value || []);
      else setHasError(true);
      const queue: Upcoming[] = [];
      if (reminders.status === 'fulfilled') {
        (reminders.value || []).filter((item: any) => item.status !== 'done' && item.status !== 'cancelled').forEach((item: any) => {
          queue.push({ id: `r${item.id}`, kind: 'reminder', title: item.text, due: item.dueAt, to: '/settings/reminders' });
        });
      }
      if (tasks.status === 'fulfilled') {
        (tasks.value || []).filter((item: any) => item.status !== 'done' && item.status !== 'cancelled').forEach((item: any) => {
          queue.push({ id: `t${item.id}`, kind: 'task', title: item.title, due: item.dueAt, to: '/settings/tasks' });
        });
      }
      queue.sort((left, right) => new Date(left.due || '2999-01-01').getTime() - new Date(right.due || '2999-01-01').getTime());
      setUpcoming(queue.slice(0, 5));
      setLoading(false);
    });
  }, []);

  const activePipelines = pipelines.filter((item) => item.enabled);
  const dateLabel = useMemo(() => {
    const label = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
    return label.slice(0, 1).toUpperCase() + label.slice(1);
  }, []);
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Доброе утро';
    if (hour < 18) return 'Добрый день';
    return 'Добрый вечер';
  }, []);
  const go = (to: string, strength: 'light' | 'medium' = 'light') => {
    haptic(strength);
    navigate(to);
  };

  return (
    <div className="oa-studio-home">
      <section className="oa-studio-hero">
        <p className="oa-studio-date">{dateLabel}</p>
        <h1>{greeting}, {user.name}</h1>
        <p className="oa-studio-lead">С чего начнём?</p>

        <button className="oa-studio-composer" onClick={() => go('/assistant', 'medium')}>
          <span><Sparkles /></span>
          <div><strong>Опишите задачу для ассистента</strong><small>Он сам подберёт агентов, инструменты и порядок работы</small></div>
          <ArrowRight />
        </button>

        <div className="oa-studio-prompts" aria-label="Быстрые действия">
          <button onClick={() => go('/assistant')}><FileText /><span>Разобрать документ</span></button>
          <button onClick={() => go('/assistant')}><Search /><span>Исследовать вопрос</span></button>
          <button onClick={() => go('/automation/wizard')}><Workflow /><span>Собрать сценарий</span></button>
        </div>
      </section>

      <HomeSolutions />

      <div className="oa-studio-home-grid">
        <section className="oa-studio-worklist">
          <header><div><h2>Продолжить работу</h2><p>Активные сценарии и ближайшие задачи</p></div><button onClick={() => go('/activity')}>Вся активность <ArrowRight /></button></header>
          <div className="oa-studio-workrows">
            {loading ? <div className="oa-studio-empty">Загружаем рабочее пространство…</div> : hasError && pipelines.length === 0 && upcoming.length === 0 ? (
              <div className="oa-studio-empty">Часть данных сейчас недоступна.</div>
            ) : pipelines.length === 0 && upcoming.length === 0 ? (
              <div className="oa-studio-empty positive"><CheckCircle2 />Здесь появятся сценарии и задачи, когда вы начнёте работу.</div>
            ) : (
              <>
                {pipelines.slice(0, 3).map((pipeline) => (
                  <button key={pipeline.id} onClick={() => go(`/automation/${pipeline.id}`)}>
                    <span className="oa-studio-workicon"><Workflow /></span>
                    <span className="oa-studio-workcopy"><strong>{pipeline.name}</strong><small>Автоматизация</small></span>
                    <span className={`oa-studio-workstatus${pipeline.enabled ? ' is-live' : ''}`}>{pipeline.enabled ? 'Работает' : 'На паузе'}</span>
                    <ArrowRight />
                  </button>
                ))}
                {upcoming.slice(0, Math.max(2, 5 - pipelines.slice(0, 3).length)).map((item) => (
                  <button key={item.id} onClick={() => go(item.to)}>
                    <span className="oa-studio-workicon">{item.kind === 'reminder' ? <Bell /> : <CheckCircle2 />}</span>
                    <span className="oa-studio-workcopy"><strong>{item.title}</strong><small>{item.kind === 'reminder' ? 'Напоминание' : 'Задача'}</small></span>
                    <span className="oa-studio-workstatus"><Clock />{relativeDue(item.due)}</span>
                    <ArrowRight />
                  </button>
                ))}
              </>
            )}
          </div>
        </section>

        <aside className="oa-studio-today">
          <header><h2>Сегодня</h2><span>{hasError ? 'Обновляется' : 'Всё спокойно'}</span></header>
          <div className="oa-studio-stats">
            <div><strong>{agents.length}</strong><span>агентов</span></div>
            <div><strong>{activePipelines.length}</strong><span>сценариев</span></div>
            <div><strong>{upcoming.length}</strong><span>в очереди</span></div>
          </div>
          <div className="oa-studio-next">
            <span>Ближайшее</span>
            {upcoming[0] ? <><strong>{upcoming[0].title}</strong><small>{relativeDue(upcoming[0].due)}</small></> : <><strong>Ничего срочного</strong><small>Очередь свободна</small></>}
          </div>
          <nav>
            <button onClick={() => go('/settings/agents')}><Bot />Настроить агентов<ArrowRight /></button>
            <button onClick={() => go('/settings/connections')}><Plug />Подключить сервис<ArrowRight /></button>
          </nav>
        </aside>
      </div>
    </div>
  );
}
